import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScreenDisplay = ({ screenId }) => {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [roomName, setRoomName] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(interval);
      clearInterval(timeInterval);
    };
  }, [screenId]);

  const fetchData = async () => {
    try {
      // Get all rooms to find the room by number
      const roomsResponse = await axios.get(`${API}/rooms`);
      const room = roomsResponse.data.find(r => r.number === screenId);

      if (!room) {
        console.error('Room not found');
        return;
      }

      setRoomName(room.name);

      // Get today's events for this room
      const eventsResponse = await axios.get(`${API}/events/room/${room.id}/today`);
      const events = eventsResponse.data;

      if (events.length === 0) {
        setCurrentEvent(null);
        setNextEvent(null);
        return;
      }

      // Find current event based on time
      const now = new Date();
      const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

      let current = null;
      let next = null;

      for (let i = 0; i < events.length; i++) {
        const event = events[i];

        // Jeśli impreza trwa w tej chwili
        if (currentTimeStr >= event.start_time && currentTimeStr < event.end_time) {
          current = event;
          // Get next event if exists
          if (i + 1 < events.length) {
            next = events[i + 1];
          }
          break;
        }

        // Jeśli poprzednia impreza się skończyła, a ta jeszcze się nie zaczęła
        // Pokaż następną imprezę jako główną
        if (i > 0 && currentTimeStr >= events[i - 1].end_time && currentTimeStr < event.start_time) {
          current = event;
          if (i + 1 < events.length) {
            next = events[i + 1];
          }
          break;
        }
      }

      // If no current event, show the first upcoming event as current
      if (!current && events.length > 0) {
        current = events[0];
        if (events.length > 1) {
          next = events[1];
        }
      }

      setCurrentEvent(current);
      setNextEvent(next);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)'
      }}
      data-testid={`screen-${screenId}`}>

      {/* Header with room name and time */}
      <div className="absolute top-8 left-0 right-0 px-12 flex justify-between items-center z-20">
        <div className="bg-purple-600 px-8 py-3 rounded-full shadow-lg"
          style={{ backgroundColor: '#6E35C0' }}>
          <span className="text-white font-bold text-2xl"
            style={{ fontFamily: 'Momo Trust Display, sans-serif' }}>
            {roomName ? roomName.toUpperCase() : `SALA ${screenId}`}
          </span>
        </div>

        {currentEvent && (
          <div className="bg-purple-600 px-8 py-3 rounded-full shadow-lg"
            style={{ backgroundColor: '#6E35C0' }}>
            <span className="text-white font-bold text-2xl"
              style={{ fontFamily: 'Momo Trust Display, sans-serif' }}>
              {currentEvent.start_time} - {currentEvent.end_time}
            </span>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-12">
        {!currentEvent ? (
          <div className="text-center" data-testid="no-events-message">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-700"
              style={{ fontFamily: 'Momo Trust Display, sans-serif' }}>
              AKTUALNIE W TEJ SALI NIE ODBYWAJĄ SIĘ ŻADNE URODZINY
            </h1>
            <p className="text-3xl md:text-4xl text-gray-600"
              style={{ fontFamily: 'Momo Trust Display, sans-serif' }}>
              Zorganizuj urodziny dla swojego dziecka na
            </p>
            <div className="inline-block bg-purple-600 mt-6 text-white px-16 py-6 rounded-full shadow-xl"
              style={{ backgroundColor: '#6E35C0' }}>
              <p className="text-3xl font-bold"
                style={{ fontFamily: 'Momo Trust Display, sans-serif' }}>
                URODZINY.AVEPARK.PL
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center w-full" data-testid="current-event">
            <p className="text-2xl md:text-3xl text-gray-500 mb-8"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              NA IMPREZĘ URODZINOWĄ ZAPRASZA
            </p>
            <h1 className="text-8xl md:text-9xl font-bold uppercase mb-16"
              style={{
                fontFamily: 'Momo Trust Display, sans-serif',
                color: '#6E35C0',
                letterSpacing: '0.02em'
              }}
              data-testid="current-event-name">
              {currentEvent.name}
            </h1>
          </div>
        )}
      </div>

      {/* Footer with next event */}
      {nextEvent && (
        <div className="absolute bottom-16 left-0 right-0 px-12 z-20">
          <p className="text-center text-xl md:text-2xl"
            style={{
              fontFamily: 'Space Grotesk, sans-serif',
              color: '#6b7280'
            }}>
            NASTĘPNA IMPREZA:{' '}
            <span className="font-bold"
              style={{ color: '#6E35C0' }}>
              {nextEvent.name.toUpperCase()}
            </span>
            {' '}
            <span className="font-bold px-4 py-2 rounded-full border-2 inline-block mx-2"
              style={{
                color: '#6E35C0',
                borderColor: '#6E35C0'
              }}>
              {nextEvent.start_time} - {nextEvent.end_time}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default ScreenDisplay;
